/*
  Page-Orchestrierung für /shiftplan_v2.
*/

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../../../AuthContext";
import { normalizeApiError } from "../api/errors";
import { setActivePeriod } from "../api/period";
import {
  getShiftplanBootstrap,
  getShiftplanDemand,
  getShiftplanEntries,
  getShiftplanWorkload,
  resetDepartmentOrder,
  resetEmployeeOrder,
  saveDayComment,
  saveSchedulerEvents,
  sendReleaseNotifications,
  updateDepartmentColor,
  updateDisplayCalendar,
  updateDepartmentOrder,
  updateEmployeeOrder,
  updateShiftColor,
  updateShiftDisplayMode,
} from "../api/shiftplanApi";
import {
  mapDemandPayload,
  mapEntriesPayload,
  mapPeriodSummaryPayload,
  mapSchedulerPayload,
  mapWorkloadPayload,
} from "../api/mappers";
import {
  addMonthsToDate,
  addWeeksToDate,
  formatMonthTitle,
  formatWeekTitle,
  getMonthDays,
  getWeekDays,
} from "../domain/date/month";
import {
  buildVisibleDepartments,
  buildVisibleEmployees,
  filterShiftsByDays,
  getDepartmentRowId,
  getShiftDepartmentRowId,
  groupPublicHolidayNamesByDate,
  groupShiftsByCell,
  sortByIdOrder,
  sortDepartmentsByIds,
} from "../domain/model/selectors";
import { buildAvailabilityByCell } from "../domain/model/availability";
import { buildConflicts } from "../domain/model/conflicts";
import { buildDemandByDay } from "../domain/model/demand";
import { groupUserHolidaysByCell } from "../domain/model/userHolidays";
import { groupAvailabilityAbsencesByCell } from "../domain/model/availabilityAbsences";
import { groupDayCommentsByDate } from "../domain/model/dayComments";
import { buildEmployeeWorkloadMap } from "../domain/model/workload";
import { buildPeriodSummary } from "../domain/model/periodSummary";
import { applyLocalShiftMoves } from "../domain/model/schedulerEvents";
import { SHIFT_DISPLAY_MODES } from "../domain/model/shiftDisplay";
import ShiftplanV2Layout from "./ShiftplanV2Layout";
import { isDemoMode } from "../../../demoMode";

const EMPTY_DATA = {
  employees: [],
  shifts: [],
  shiftTemplates: [],
  absenceOptions: [],
  userHolidayTypes: [],
  userHolidays: [],
  availabilityEntries: [],
  availabilityDefaults: [],
  availabilityAbsences: [],
  departments: [],
  publicHolidays: [],
  dayComments: [],
  meta: {},
};

const VIEW_MODES = Object.freeze({
  MONTH: "month",
  WEEK: "week",
});

const ROW_MODES = Object.freeze({
  EMPLOYEE: "employee",
  DEPARTMENT: "department",
});

export default function ShiftplanV2Page() {
  /*
    Die Berechtigung steht schon beim Anmelden fest und liegt lokal vor. Ohne
    sie wäre die Werkzeugleiste während des Ladens unvollständig – der Knopf
    "Automatisch planen" käme erst mit der Antwort dazu und schöbe die Reihe.
    Sobald die Antwort da ist, gilt wieder der Wert des Servers.
  */
  const { user } = useContext(AuthContext) || {};
  const [referenceDate, setReferenceDate] = useState(() => new Date());
  const [viewMode, setViewMode] = useState(VIEW_MODES.MONTH);
  const [rowMode, setRowMode] = useState(ROW_MODES.EMPLOYEE);
  const [data, setData] = useState(EMPTY_DATA);
  const [demandEntries, setDemandEntries] = useState([]);
  const [backendWorkload, setBackendWorkload] = useState({ periodKey: "", rows: {}, periodSummary: null });
  const [workloadRefreshKey, setWorkloadRefreshKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [draftShifts, setDraftShifts] = useState([]);
  const [planVersion, setPlanVersion] = useState(0);

  /*
    Antworten können sich überholen. Der Plan lädt seit dem Zeitfenster bei
    jedem Periodenwechsel nach; blättert jemand dreimal schnell weiter, sind
    drei Anfragen unterwegs und die zuletzt eintreffende gewinnt — nicht die
    zuletzt gestellte. Dann zeigt die Tabelle den Februar, während oben März
    steht. Jede Anfrage zieht deshalb eine Nummer, und nur die aktuellste darf
    den Zustand schreiben.
  */
  const requestSequenceRef = useRef(0);
  const nextRequestId = useCallback(() => (requestSequenceRef.current += 1), []);
  const applyPlanPayload = useCallback((requestId, payload, { refreshWorkload = false } = {}) => {
    const mapped = mapSchedulerPayload(payload);
    // Überholt: eine neuere Anfrage ist schon unterwegs oder bereits verarbeitet.
    // Das Ergebnis geht trotzdem an den Aufrufer zurück, nur der Zustand bleibt.
    if (requestId !== requestSequenceRef.current) return mapped;

    setData(mapped);
    setPlanVersion((current) => current + 1);
    if (refreshWorkload) {
      setBackendWorkload({ periodKey: "", rows: {}, periodSummary: null });
      setWorkloadRefreshKey((current) => current + 1);
    }
    return mapped;
  }, []);

  /*
    Die Ladeanzeige zählt mit, statt sich an der Anfragenummer zu orientieren.

    Die Nummer entscheidet, wer den Zustand schreiben darf — und sie steigt auch
    bei jeder Änderung (Farbe, Speichern). Hinge die Anzeige daran, würde eine
    Farbänderung während des Ladens die Nummer weiterdrehen, das "finally" des
    Ladevorgangs würde sich für überholt halten und der Wartekreis bliebe für
    immer stehen. Gezählt wird deshalb schlicht, was noch unterwegs ist.
  */
  const inFlightLoadsRef = useRef(0);
  const loadShiftplan = useCallback(async () => {
    const requestId = nextRequestId();
    inFlightLoadsRef.current += 1;
    setIsLoading(true);
    setError("");
    try {
      applyPlanPayload(requestId, await getShiftplanBootstrap(), { refreshWorkload: true });
    } catch (loadError) {
      if (requestId !== requestSequenceRef.current) return;
      const apiError = normalizeApiError(loadError);
      setError(apiError.message);
    } finally {
      inFlightLoadsRef.current = Math.max(0, inFlightLoadsRef.current - 1);
      if (!inFlightLoadsRef.current) setIsLoading(false);
    }
  }, [applyPlanPayload, nextRequestId]);

  const days = useMemo(
    () => (viewMode === VIEW_MODES.WEEK ? getWeekDays(referenceDate) : getMonthDays(referenceDate)),
    [referenceDate, viewMode]
  );
  const periodLabel = useMemo(
    () => (viewMode === VIEW_MODES.WEEK ? formatWeekTitle(referenceDate) : formatMonthTitle(referenceDate)),
    [referenceDate, viewMode]
  );
  const periodStartDate = days[0]?.key || "";
  const periodEndDate = days[days.length - 1]?.key || "";
  const workloadPeriodKey = `${periodStartDate}::${periodEndDate}`;

  /*
    Geladen wird die angezeigte Periode plus die folgende.

    Wer plant, geht fast immer vorwärts — der nächste Monat ist der, den man
    füllt. Er liegt deshalb schon bereit, wenn man dorthin blättert: Das Raster
    zeigt ihn sofort, und die Anfrage für den übernächsten läuft im
    Hintergrund. Die Ladefläche erscheint nur, wenn wirklich nichts da ist
    (siehe ShiftplanV2Layout), also beim ersten Aufruf und beim Sprung in einen
    weit entfernten Monat.

    Rückwärts wird nicht vorgeladen. Das kostete ein weiteres Drittel Nutzlast
    für den selteneren Weg; wer zurückblättert, wartet einmal kurz.
  */
  const nextPeriodDays = useMemo(
    () =>
      viewMode === VIEW_MODES.WEEK
        ? getWeekDays(addWeeksToDate(referenceDate, 1))
        : getMonthDays(addMonthsToDate(referenceDate, 1)),
    [referenceDate, viewMode]
  );
  const loadEndDate = nextPeriodDays[nextPeriodDays.length - 1]?.key || periodEndDate;

  /*
    Das Fenster wird gesetzt, bevor geladen wird: An ihm hängen auch die
    Aufrufe aus den Bedienfeldern (Farben, Absenztypen), die ebenfalls mit dem
    vollen Plan antworten.
  */
  useEffect(() => {
    if (!periodStartDate || !loadEndDate) return;
    setActivePeriod({ startDate: periodStartDate, endDate: loadEndDate });
    loadShiftplan();
  }, [loadEndDate, loadShiftplan, periodStartDate]);
  const periodShifts = useMemo(() => filterShiftsByDays(data.shifts, days), [data.shifts, days]);
  const departmentOrder = useMemo(
    () => data.departments.map((department) => getDepartmentRowId(department?.name ?? department?.label ?? department)),
    [data.departments]
  );
  const vacationAbsenceIds = useMemo(
    () =>
      new Set(
        data.absenceOptions
          .filter((option) => option.isVacation)
          .map((option) => String(option.id))
      ),
    [data.absenceOptions]
  );
  const visibleRows = useMemo(
    () => {
      if (rowMode === ROW_MODES.DEPARTMENT) {
        return buildVisibleDepartments({
          departments: data.departments,
          shifts: periodShifts,
          query: searchQuery,
          departmentOrder,
          includeUnassigned: true,
        });
      }

      return buildVisibleEmployees({
        employees: data.employees,
        shifts: periodShifts,
        query: searchQuery,
      });
    },
    [data.departments, data.employees, departmentOrder, periodShifts, rowMode, searchQuery]
  );
  const orderDepartmentRows = useMemo(
    () =>
      buildVisibleDepartments({
        departments: data.departments,
        shifts: data.shifts,
        departmentOrder,
      }),
    [data.departments, data.shifts, departmentOrder]
  );
  const visibleRowIds = useMemo(
    () => new Set(visibleRows.map((row) => row.id)),
    [visibleRows]
  );
  const visibleShifts = useMemo(
    () => {
      if (rowMode === ROW_MODES.DEPARTMENT) {
        return periodShifts.filter((shift) => visibleRowIds.has(getShiftDepartmentRowId(shift)));
      }

      return periodShifts.filter((shift) => visibleRowIds.has(shift.employeeId));
    },
    [periodShifts, rowMode, visibleRowIds]
  );
  const shiftsByCell = useMemo(() => groupShiftsByCell(visibleShifts, rowMode), [rowMode, visibleShifts]);
  const availabilityByCell = useMemo(
    () =>
      buildAvailabilityByCell({
        availabilityEntries: data.availabilityEntries,
        availabilityDefaults: data.availabilityDefaults,
        companyAvailabilityMode: data.meta?.availabilityMode,
        userAvailabilityModes: data.meta?.userAvailabilityModes,
        employees: data.employees,
        days,
      }),
    [
      data.availabilityDefaults,
      data.availabilityEntries,
      data.employees,
      data.meta?.availabilityMode,
      data.meta?.userAvailabilityModes,
      days,
    ]
  );
  const userHolidaysByCell = useMemo(
    () => groupUserHolidaysByCell({ holidays: data.userHolidays, days }),
    [data.userHolidays, days]
  );
  const availabilityAbsencesByCell = useMemo(
    () => groupAvailabilityAbsencesByCell({ entries: data.availabilityAbsences, days }),
    [data.availabilityAbsences, days]
  );
  const publicHolidayNamesByDate = useMemo(
    () => groupPublicHolidayNamesByDate(data.publicHolidays),
    [data.publicHolidays]
  );
  const dayCommentsByDate = useMemo(() => groupDayCommentsByDate(data.dayComments), [data.dayComments]);
  const localWorkloadByEmployeeId = useMemo(
    () => {
      if (rowMode !== ROW_MODES.EMPLOYEE) return {};

      return buildEmployeeWorkloadMap({
        employees: visibleRows,
        shifts: visibleShifts,
        days,
        workingDaysPerWeek: data.meta?.workingDays || 5,
        absenceOptions: data.absenceOptions,
        vacationAbsenceIds,
        userHolidays: data.userHolidays,
      });
    },
    [data.absenceOptions, data.meta?.workingDays, data.userHolidays, days, rowMode, vacationAbsenceIds, visibleRows, visibleShifts]
  );
  const localPeriodSummary = useMemo(
    () =>
      buildPeriodSummary({
        employees: data.employees,
        shifts: periodShifts,
        days,
        workingDaysPerWeek: data.meta?.workingDays || 5,
        absenceOptions: data.absenceOptions,
        vacationAbsenceIds,
        userHolidays: data.userHolidays,
      }),
    [data.absenceOptions, data.employees, data.meta?.workingDays, data.userHolidays, days, periodShifts, vacationAbsenceIds]
  );
  useEffect(() => {
    if (!periodStartDate || !periodEndDate) return undefined;

    let isCurrent = true;
    getShiftplanDemand({ startDate: periodStartDate, endDate: periodEndDate })
      .then((payload) => {
        if (!isCurrent) return;
        setDemandEntries(mapDemandPayload(payload));
      })
      .catch(() => {
        if (!isCurrent) return;
        setDemandEntries([]);
      });

    return () => {
      isCurrent = false;
    };
  }, [periodEndDate, periodStartDate]);
  const demandModel = useMemo(
    () => buildDemandByDay({ demandEntries, shifts: data.shifts, days }),
    [data.shifts, days, demandEntries]
  );
  const conflictModel = useMemo(
    () =>
      buildConflicts({
        shifts: periodShifts,
        userHolidays: data.userHolidays,
        days,
        availabilityEntries: data.availabilityEntries,
        availabilityDefaults: data.availabilityDefaults,
        companyAvailabilityMode: data.meta?.availabilityMode,
        userAvailabilityModes: data.meta?.userAvailabilityModes,
      }),
    [
      data.availabilityDefaults,
      data.availabilityEntries,
      data.meta?.availabilityMode,
      data.meta?.userAvailabilityModes,
      data.userHolidays,
      days,
      periodShifts,
    ]
  );
  useEffect(() => {
    if (!periodStartDate || !periodEndDate || !data.employees.length) return undefined;

    let isCurrent = true;
    getShiftplanWorkload({ startDate: periodStartDate, endDate: periodEndDate })
      .then((payload) => {
        if (!isCurrent) return;
        setBackendWorkload({
          periodKey: workloadPeriodKey,
          rows: mapWorkloadPayload(payload),
          periodSummary: mapPeriodSummaryPayload(payload),
        });
      })
      .catch(() => {
        if (!isCurrent) return;
        setBackendWorkload({ periodKey: "", rows: {}, periodSummary: null });
      });

    return () => {
      isCurrent = false;
    };
  }, [data.employees.length, periodEndDate, periodStartDate, workloadPeriodKey, workloadRefreshKey]);
  const workloadByEmployeeId = useMemo(() => {
    if (rowMode !== ROW_MODES.EMPLOYEE) return {};
    if (backendWorkload.periodKey !== workloadPeriodKey) return localWorkloadByEmployeeId;
    return {
      ...localWorkloadByEmployeeId,
      ...backendWorkload.rows,
    };
  }, [backendWorkload, localWorkloadByEmployeeId, rowMode, workloadPeriodKey]);
  const periodSummary = useMemo(() => {
    if (backendWorkload.periodKey !== workloadPeriodKey || !backendWorkload.periodSummary) return localPeriodSummary;
    return {
      ...localPeriodSummary,
      ...backendWorkload.periodSummary,
    };
  }, [backendWorkload.periodKey, backendWorkload.periodSummary, localPeriodSummary, workloadPeriodKey]);
  const handlePreviousPeriod = useCallback(() => {
    setReferenceDate((current) =>
      viewMode === VIEW_MODES.WEEK ? addWeeksToDate(current, -1) : addMonthsToDate(current, -1)
    );
  }, [viewMode]);
  const handleNextPeriod = useCallback(() => {
    setReferenceDate((current) =>
      viewMode === VIEW_MODES.WEEK ? addWeeksToDate(current, 1) : addMonthsToDate(current, 1)
    );
  }, [viewMode]);
  /*
    Reihenfolge aus den Einstellungen: sofort im Raster, dann gespeichert.
    Der ganze Plan muss dafür nicht neu kommen – die Reihenfolge ist alles,
    was sich ändert. Früher hielt das Neuladen die Einstellung nach jedem
    einzelnen Schritt an.

    Lief beim Speichern noch ein Ladevorgang, bringt er die alte Reihenfolge
    mit; dann wird danach einmal neu geladen. Scheitert das Speichern, holt der
    Plan den Stand des Servers zurück.
  */
  const saveOrderLocallyFirst = useCallback(
    async (applyLocalOrder, saveOrder) => {
      setData(applyLocalOrder);
      try {
        await saveOrder();
      } catch (saveError) {
        loadShiftplan();
        throw saveError;
      }
      if (inFlightLoadsRef.current > 0) loadShiftplan();
    },
    [loadShiftplan]
  );
  const handleSaveEmployeeOrder = useCallback(
    (orderedRows) => {
      const orderedIds = orderedRows.map((row) => String(row.id));
      return saveOrderLocallyFirst(
        (current) => ({ ...current, employees: sortByIdOrder(current.employees, orderedIds) }),
        () =>
          updateEmployeeOrder(
            orderedRows.map((row, index) => ({
              id: Number(row.id),
              employee_order_number: index + 1,
            }))
          )
      );
    },
    [saveOrderLocallyFirst]
  );
  const handleResetEmployeeOrder = useCallback(async () => {
    await resetEmployeeOrder();
    await loadShiftplan();
  }, [loadShiftplan]);
  const handleSaveDepartmentOrder = useCallback(
    (orderedRows) => {
      const orderedIds = orderedRows.map((row) => String(row.id));
      return saveOrderLocallyFirst(
        (current) => ({ ...current, departments: sortDepartmentsByIds(current.departments, orderedIds) }),
        () =>
          updateDepartmentOrder(
            orderedRows.map((row, index) => ({
              id: Number(row.id),
              department_order_number: index + 1,
            }))
          )
      );
    },
    [saveOrderLocallyFirst]
  );
  const handleResetDepartmentOrder = useCallback(async () => {
    await resetDepartmentOrder();
    await loadShiftplan();
  }, [loadShiftplan]);
  const handleDisplayCalendarChange = useCallback(async (displayCalendar) => {
    const requestId = nextRequestId();
    applyPlanPayload(requestId, await updateDisplayCalendar(displayCalendar));
  }, [applyPlanPayload, nextRequestId]);
  const handleShiftDisplayModeChange = useCallback(async (shiftDisplayMode) => {
    const requestId = nextRequestId();
    applyPlanPayload(requestId, await updateShiftDisplayMode(shiftDisplayMode));
  }, [applyPlanPayload, nextRequestId]);
  const handleDepartmentColorChange = useCallback(async ({ id, colorHex }) => {
    const requestId = nextRequestId();
    applyPlanPayload(requestId, await updateDepartmentColor({ id, colorHex }));
  }, [applyPlanPayload, nextRequestId]);
  const handleShiftColorChange = useCallback(async ({ id, colorHex }) => {
    const requestId = nextRequestId();
    applyPlanPayload(requestId, await updateShiftColor({ id, colorHex }));
  }, [applyPlanPayload, nextRequestId]);
  const handleSaveSchedulerEvents = useCallback(async (events, options = {}) => {
    if (!Array.isArray(events) || !events.length) return;

    setError("");
    if (isDemoMode() && Array.isArray(options.localShiftMoves) && options.localShiftMoves.length) {
      setData((current) => ({
        ...current,
        shifts: applyLocalShiftMoves(current.shifts, options.localShiftMoves),
      }));
      setBackendWorkload({ periodKey: "", rows: {}, periodSummary: null });
      return { demoLocal: true };
    }

    const requestId = nextRequestId();
    return applyPlanPayload(requestId, await saveSchedulerEvents(events), { refreshWorkload: true });
  }, [applyPlanPayload, nextRequestId]);
  const handleSendReleaseNotifications = useCallback(
    (userIds) => sendReleaseNotifications(userIds),
    []
  );
  const handleSaveDayComment = useCallback(async (dateKey, comment) => {
    const requestId = nextRequestId();
    applyPlanPayload(requestId, await saveDayComment(dateKey, comment));
  }, [applyPlanPayload, nextRequestId]);

  /*
    Zeiträume ausserhalb der angezeigten Periode — auf Abruf statt auf Vorrat.

    Vier Ansichten brauchen mehr als das, was auf dem Raster steht: die
    Statistik rechnet auf ein Kalenderjahr, "Periode kopieren" liest eine frei
    gewählte Quellperiode, der Export einen gewählten Bereich, und die Freigabe
    braucht alle offenen Entwürfe. Früher lagen die Daten ohnehin alle im
    Speicher; jetzt holt sich jede Ansicht beim Öffnen genau ihren Ausschnitt.

    Die Stammdaten für die Zuordnung (Personen, Vorlagen, Farben) kommen aus
    dem geladenen Stand — sie hängen nicht am Zeitraum. Der Verweis liegt in
    einem ref, damit diese Funktion stabil bleibt und die Dialoge nicht bei
    jeder Änderung am Plan neu laden.
  */
  const dataRef = useRef(data);
  dataRef.current = data;
  const loadPlanRange = useCallback(async ({ startDate, endDate, draftsOnly = false } = {}) => {
    const empty = { shifts: [], userHolidays: [], publicHolidays: [], dayComments: [] };
    if (!draftsOnly && (!startDate || !endDate)) return empty;

    const payload = await getShiftplanEntries({ startDate, endDate, draftsOnly });
    const { employees, shiftTemplates, meta } = dataRef.current;
    return mapEntriesPayload(payload, { employees, shiftTemplates, colorMap: meta?.colorMap });
  }, []);

  /*
    Die offenen Entwürfe, unabhängig vom angezeigten Zeitraum.

    Der Knopf "Freigeben" nennt ihre Zahl, ohne dass man ihn öffnet, und
    "Alle freigeben" muss auch Entwürfe erwischen, die weiter in der Zukunft
    liegen. Sie kommen deshalb als eigene, kleine Anfrage — nicht dadurch, dass
    der Plan vorsorglich alles lädt. Nach jedem Speichern neu, weil eine
    Freigabe die Menge verändert.
  */
  useEffect(() => {
    if (!planVersion) return undefined;

    let isCurrent = true;
    loadPlanRange({ draftsOnly: true })
      .then((range) => {
        if (isCurrent) setDraftShifts(range.shifts);
      })
      .catch(() => {
        if (isCurrent) setDraftShifts([]);
      });

    return () => {
      isCurrent = false;
    };
  }, [loadPlanRange, planVersion]);

  return (
    <ShiftplanV2Layout
      viewMode={viewMode}
      rowMode={rowMode}
      referenceDate={referenceDate}
      periodLabel={periodLabel}
      searchQuery={searchQuery}
      isLoading={isLoading}
      error={error}
      days={days}
      rows={visibleRows}
      employeeOrderRows={data.employees}
      departmentOrderRows={orderDepartmentRows}
      departments={data.departments}
      shiftTemplates={data.shiftTemplates}
      absenceOptions={data.absenceOptions}
      userHolidayTypes={data.userHolidayTypes}
      userHolidays={data.userHolidays}
      publicHolidayNamesByDate={publicHolidayNamesByDate}
      dayCommentsByDate={dayCommentsByDate}
      onSaveDayComment={handleSaveDayComment}
      shifts={visibleShifts}
      allShifts={data.shifts}
      draftShifts={draftShifts}
      onLoadPlanRange={loadPlanRange}
      shiftsByCell={shiftsByCell}
      availabilityEntries={data.availabilityEntries}
      availabilityDefaults={data.availabilityDefaults}
      availabilityByCell={availabilityByCell}
      companyAvailabilityMode={data.meta?.availabilityMode}
      userAvailabilityModes={data.meta?.userAvailabilityModes}
      userHolidaysByCell={userHolidaysByCell}
      availabilityAbsencesByCell={availabilityAbsencesByCell}
      workloadByEmployeeId={workloadByEmployeeId}
      demandModel={demandModel}
      conflictModel={conflictModel}
      periodSummary={periodSummary}
      workingDaysPerWeek={data.meta?.workingDays || 5}
      vacationAbsenceIds={vacationAbsenceIds}
      displayCalendar={data.meta?.displayCalendar || "department"}
      locationName={data.meta?.locationName || ""}
      companyName={user?.company_name || user?.company || ""}
      shiftDisplayMode={data.meta?.shiftDisplayMode || SHIFT_DISPLAY_MODES.TIME_DEPARTMENT}
      onViewModeChange={setViewMode}
      onRowModeChange={setRowMode}
      onSearchQueryChange={setSearchQuery}
      onPreviousPeriod={handlePreviousPeriod}
      onNextPeriod={handleNextPeriod}
      onPeriodSelect={setReferenceDate}
      onSaveEmployeeOrder={handleSaveEmployeeOrder}
      onResetEmployeeOrder={handleResetEmployeeOrder}
      onSaveDepartmentOrder={handleSaveDepartmentOrder}
      onResetDepartmentOrder={handleResetDepartmentOrder}
      onDisplayCalendarChange={handleDisplayCalendarChange}
      onShiftDisplayModeChange={handleShiftDisplayModeChange}
      onDepartmentColorChange={handleDepartmentColorChange}
      onShiftColorChange={handleShiftColorChange}
      accessLevel={data.meta?.accessLevel || user?.access_level || ""}
      whatsappSubscription={Boolean(data.meta?.whatsappSubscription)}
      wiesnshopExcelExport={Boolean(data.meta?.wiesnshopExcelExport)}
      onPublicHolidaysChanged={loadShiftplan}
      onSolverTransferred={loadShiftplan}
      onSaveSchedulerEvents={handleSaveSchedulerEvents}
      onSendReleaseNotifications={handleSendReleaseNotifications}
    />
  );
}
